import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../config/siteConfig';

const LOGO_WHITE = siteConfig.logo;

const Footer: React.FC = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-maat-blue border-t border-maat-gold/10 text-maat-stone/70 py-16 px-6">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="inline-flex items-center space-x-3 mb-6">
              <img src={LOGO_WHITE} alt="Met Maät Logo" className="h-10 w-auto" />
              <span translate="no" className="text-maat-stone font-serif text-2xl font-bold">{siteConfig.fullName}</span>
            </Link>
            <p className="max-w-md mb-8 leading-relaxed">
              {t.footer.desc}
            </p>
            <div className="flex space-x-4">
              <a href={siteConfig.social.linkedin} target="_blank" rel="noopener noreferrer" className="p-3 bg-maat-stone/5 rounded-full hover:bg-maat-gold/20 transition-colors">
                <Linkedin size={20} className="text-maat-gold" />
              </a>
              <a href={`mailto:${siteConfig.social.email}`} className="p-3 bg-maat-stone/5 rounded-full hover:bg-maat-gold/20 transition-colors">
                <Mail size={20} className="text-maat-gold" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-maat-stone font-serif text-lg font-bold mb-6">{t.footer.navTitle}</h4>
            <ul className="space-y-4 text-sm">
              {siteConfig.navigation.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="hover:text-maat-gold transition-colors">
                    {(t.nav as any)[link.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-maat-stone font-serif text-lg font-bold mb-6">{t.footer.contactTitle}</h4>
            <p className="mb-4 text-sm">{t.footer.contactText}</p>
            <Link to="/contact" className="flex items-center text-maat-gold font-bold hover:underline text-sm uppercase tracking-wider">
              {t.nav.contact} <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>

        <div className="pt-8 border-t border-maat-gold/5 flex flex-col md:flex-row justify-between items-center text-xs tracking-wide">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
            <p>&copy; {new Date().getFullYear()} {siteConfig.fullName}. {t.footer.rights}</p>
            <div className="hidden md:block w-px h-3 bg-maat-gold/20"></div>
            <div className="flex items-center space-x-3">
              {siteConfig.legalLinks.map((link, idx) => (
                <React.Fragment key={link.path}>
                  <Link
                    to={link.path}
                    className="hover:text-maat-gold transition-colors underline decoration-maat-gold/20 underline-offset-4"
                  >
                    {(t.legal as any)[link.key]}
                  </Link>
                  {idx < siteConfig.legalLinks.length - 1 && <span className="text-maat-gold/20">|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          {siteConfig.taglineKey && (
            <div className="mt-6 md:mt-0">
              <span className="italic font-serif opacity-40">{(t.footer as any)[siteConfig.taglineKey.split('.')[1]]}</span>
            </div>
          )}
        </div>

        <div className="pt-4 text-xs tracking-wide opacity-60 text-center md:text-left">
          <p>
            {siteConfig.legalName} &middot; {t.company.kvkLabel} {siteConfig.kvk}
            {siteConfig.vat ? ` \u00b7 ${t.company.vatLabel} ${siteConfig.vat}` : ''}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;